import React from "react";
import usePageTitle from "../../hooks/usePageTitle";
import LegalLayout, { Section, P, UL, Callout, ContactCard } from "./LegalLayout";

const SECTIONS = [
  { id: "acceptance",    title: "Acceptance of Terms" },
  { id: "service",       title: "Description of Service" },
  { id: "account",       title: "Your Account" },
  { id: "acceptable",    title: "Acceptable Use" },
  { id: "prohibited",    title: "Prohibited Activities" },
  { id: "data",          title: "Your Data" },
  { id: "ip",            title: "Intellectual Property" },
  { id: "disclaimers",   title: "Disclaimers" },
  { id: "liability",     title: "Limitation of Liability" },
  { id: "termination",   title: "Termination" },
  { id: "governing",     title: "Governing Law" },
  { id: "changes",       title: "Changes to Terms" },
  { id: "contact",       title: "Contact" },
];

const BANNER = `${process.env.PUBLIC_URL}/illustrations/legal-terms-banner.svg`;

const META = [
  { label: "Last updated",  value: "March 2026" },
  { label: "Jurisdiction",  value: "India" },
  { label: "Governing law", value: "Information Technology Act, 2000" },
];

const CONTACT = [
  { label: "Email",         value: "legal@easybilling.in" },
  { label: "Product",       value: "EasyBilling" },
  { label: "Country",       value: "India" },
  { label: "Response time", value: "Within 30 business days" },
];

export default function TermsOfService() {
  usePageTitle("Terms of Service");
  return (
    <LegalLayout
      current="terms"
      eyebrow="Terms of Service"
      title="Terms governing your use of EasyBilling"
      lede="Please read these terms carefully before using EasyBilling. By creating an account, you agree to be bound by these terms. If you disagree with any part, do not use the service."
      meta={META}
      sections={SECTIONS}
      banner={BANNER}
    >
      <Section id="acceptance" title="Acceptance of Terms">
        <P>These Terms of Service ("Terms") constitute a legally binding agreement between you ("User", "you") and EasyBilling ("we", "us", "our") governing your access to and use of the EasyBilling platform, accessible at easybilling.in.</P>
        <P>By registering an account, clicking "Create Account", or otherwise using the service, you confirm that:</P>
        <UL items={[
          "You have read and understood these Terms",
          "You are at least 18 years of age",
          "You have the legal authority to enter into this agreement on behalf of yourself or the business entity you represent",
          "You agree to comply with all applicable Indian laws including the Information Technology Act, 2000, the GST Act, 2017, and the Companies Act, 2013 where applicable",
        ]} />
      </Section>

      <Section id="service" title="Description of Service">
        <P>EasyBilling is a cloud-based business management and GST invoicing platform that provides:</P>
        <UL items={[
          "GST-compliant invoice generation and management",
          "Purchase order and supplier management",
          "Inventory and stock tracking",
          "Client and supplier ledgers",
          "Financial reporting including P&L, cash flow, and balance sheet",
          "GST compliance reports including GSTR-1, GSTR-3B, and HSN Summary",
          "Sales team and commission management",
        ]} />
        <Callout type="info">
          EasyBilling is a tool to help you create and manage invoices and business records. 
          It is not a licensed tax consultant, Chartered Accountant, or legal advisor. 
          For tax filing, compliance decisions, and financial advice, please consult a qualified professional.
        </Callout>
        <P>We reserve the right to modify, suspend, or discontinue any feature of the service at any time with reasonable notice to users.</P>
      </Section>

      <Section id="account" title="Your Account">
        <P><strong>Registration</strong> — You must provide accurate and complete information when creating your account. You are responsible for keeping your account information current.</P>
        <P><strong>Security</strong> — You are responsible for:</P>
        <UL items={[
          "Maintaining the confidentiality of your password",
          "All activity that occurs under your account",
          "Immediately notifying us at legal@easybilling.in if you suspect unauthorised access to your account",
        ]} />
        <P><strong>One account per business</strong> — Each business entity should have one primary account. You may not create multiple accounts to circumvent usage limits or policies.</P>
        <P><strong>Account accuracy</strong> — Business details you enter, including GSTIN and PAN, are used to generate legal documents. You are solely responsible for the accuracy of this information and any consequences arising from incorrect details on invoices or tax documents.</P>
      </Section>

      <Section id="acceptable" title="Acceptable Use">
        <P>EasyBilling is designed for legitimate business invoicing and financial management. You agree to use the service only for:</P>
        <UL items={[
          "Generating invoices for goods or services actually provided by your business",
          "Managing your legitimate business relationships with clients and suppliers",
          "Tracking inventory and stock for products you lawfully own or sell",
          "Generating GST reports to assist with your legal tax compliance obligations",
        ]} />
        <P>You are solely responsible for ensuring that all invoices, tax documents, and business records you create using EasyBilling are accurate and compliant with applicable Indian law.</P>
      </Section>

      <Section id="prohibited" title="Prohibited Activities">
        <P>You must not use EasyBilling for:</P>
        <UL items={[
          "Generating fake, fraudulent, or backdated invoices for any purpose including tax evasion",
          "Creating invoices for goods or services not actually provided",
          "Misrepresenting GSTIN, PAN, or any business registration details",
          "Any activity that violates the GST Act, Income Tax Act, or any other applicable Indian law",
          "Uploading or storing illegal content, malware, or content that infringes third-party intellectual property rights",
          "Attempting to gain unauthorised access to other users' accounts or data",
          "Reverse engineering, decompiling, or attempting to extract the source code of EasyBilling",
          "Using automated scripts, bots, or scrapers to access the service without our written permission",
          "Reselling or sublicensing access to EasyBilling to third parties",
        ]} />
        <Callout type="warning">
          Creating fraudulent invoices or using EasyBilling to facilitate tax evasion is a serious 
          criminal offence under Indian law. We will cooperate fully with law enforcement authorities 
          in any such investigation and will terminate accounts found to be engaged in fraudulent activity.
        </Callout>
      </Section>

      <Section id="data" title="Your Data">
        <P><strong>Ownership</strong> — All business data you create within EasyBilling — including invoices, client records, product catalogues, and financial reports — belongs to you. We do not claim any ownership over your business data.</P>
        <P><strong>Licence to us</strong> — By using EasyBilling, you grant us a limited, non-exclusive licence to store, process, and display your data solely for the purpose of providing the service to you. We do not use your business data for any other purpose.</P>
        <P><strong>Export</strong> — You can export your invoices as PDFs and your reports as CSV files at any time from within the application. We do not hold your data hostage.</P>
        <P><strong>Accuracy</strong> — You are responsible for the accuracy of all data you enter. EasyBilling generates documents based on the information you provide. We are not liable for errors in tax documents or invoices arising from inaccurate data entry.</P>
        <P>Please refer to our <a href="/privacy">Privacy Policy</a> for full details on how we handle your personal and business data.</P>
      </Section>

      <Section id="ip" title="Intellectual Property">
        <P><strong>EasyBilling platform</strong> — The EasyBilling software, design, user interface, branding, and all related intellectual property are owned by EasyBilling and protected under applicable Indian and international intellectual property laws.</P>
        <P><strong>Your content</strong> — Your business name, logo, and business data remain your property at all times.</P>
        <P><strong>Restrictions</strong> — You may not copy, reproduce, distribute, or create derivative works from any part of the EasyBilling platform without our express written consent.</P>
        <P><strong>Feedback</strong> — If you provide suggestions or feedback about EasyBilling, you grant us a perpetual, royalty-free licence to use that feedback to improve the service without any obligation to you.</P>
      </Section>

      <Section id="disclaimers" title="Disclaimers">
        <Callout type="warning">
          EasyBilling is provided "as is" and "as available" without warranties of any kind, 
          either express or implied.
        </Callout>
        <P>We do not warrant that:</P>
        <UL items={[
          "The service will be uninterrupted, error-free, or completely secure at all times",
          "The GST calculations or report formats will always reflect the latest GSTN circulars or notifications — users should verify compliance with their CA",
          "Data stored on our servers will never be lost — we recommend maintaining your own backups of critical business records",
          "The service will meet every specific requirement of your business",
        ]} />
        <P>EasyBilling is a productivity tool, not a substitute for professional legal, accounting, or tax advice. Always consult a qualified Chartered Accountant for tax filing decisions.</P>
      </Section>

      <Section id="liability" title="Limitation of Liability">
        <P>To the maximum extent permitted by applicable Indian law, EasyBilling and its founders, employees, and agents shall not be liable for:</P>
        <UL items={[
          "Any indirect, incidental, special, or consequential damages arising from your use of the service",
          "Loss of business, revenue, profits, data, or goodwill",
          "Penalties, interest, or fines imposed by tax authorities arising from incorrect invoices or returns",
          "Losses arising from unauthorised access to your account due to your failure to maintain password security",
          "Service interruptions, data loss, or technical failures beyond our reasonable control",
        ]} />
        <P>Our total liability to you for any claim arising from your use of EasyBilling shall not exceed the amount you paid us in the 12 months preceding the claim, or ₹1,000, whichever is greater.</P>
      </Section>

      <Section id="termination" title="Termination">
        <P><strong>By you</strong> — You may stop using EasyBilling and request account deletion at any time by contacting legal@easybilling.in. We will process your request in accordance with our data retention policy.</P>
        <P><strong>By us</strong> — We reserve the right to suspend or terminate your account immediately and without notice if:</P>
        <UL items={[
          "You violate these Terms, particularly the prohibited activities",
          "We receive a valid legal order requiring termination",
          "We reasonably suspect fraudulent or illegal activity on your account",
          "Your account remains inactive for more than 24 consecutive months",
        ]} />
        <P>Upon termination, your right to access the service ceases immediately. Your data will be handled in accordance with our Privacy Policy and applicable data retention obligations under Indian law.</P>
      </Section>

      <Section id="governing" title="Governing Law">
        <P>These Terms are governed by and construed in accordance with the laws of India. Any dispute arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in India.</P>
        <P>We encourage you to contact us first at legal@easybilling.in to resolve any dispute informally before pursuing legal action.</P>
      </Section>

      <Section id="changes" title="Changes to Terms">
        <P>We may update these Terms from time to time to reflect changes in the service, applicable law, or our business practices. When we make material changes:</P>
        <UL items={[
          "We will update the 'Last updated' date at the top of this page",
          "We will display a notice within the application for logged-in users",
          "We will send an email notification for significant changes",
        ]} />
        <P>If you continue to use EasyBilling after updated Terms are posted, you accept the revised Terms. If you disagree with any changes, you must stop using the service and may request account deletion.</P>
      </Section>

      <Section id="contact" title="Contact">
        <P>For questions, concerns, or requests related to these Terms of Service:</P>
        <ContactCard rows={CONTACT} />
      </Section>
    </LegalLayout>
  );
}
